// externals
import type { Reducer } from 'redux';
import type { IResponsePayment } from '@makemydeal/dr-platform-types';

// libraries
import { bootstrapActionTypes } from '@makemydeal/dr-shared-store';

// types
import type {
    FirstPencilIncentiveOverride,
    FirstPencilProgramsOfferType,
    FirstPencilProgramsState,
    FirstPencilProgramsTabLeft,
    FirstPencilState
} from '../types/firstPencilTypes';

// consts/enums
import {
    FIRST_PENCIL_APPLY_FAILED,
    FIRST_PENCIL_APPLY_SUCCEEDED,
    FIRST_PENCIL_CANCELLED,
    FIRST_PENCIL_CREATE_CONFIRMED,
    FIRST_PENCIL_INCENTIVE_OVERRIDDEN,
    FIRST_PENCIL_INCENTIVES_SELECTED,
    FIRST_PENCIL_INTERCEPT_OPENED,
    FIRST_PENCIL_MANUAL_INCENTIVE_ADDED,
    FIRST_PENCIL_MANUAL_INCENTIVE_UPDATED,
    FIRST_PENCIL_OFFER_TYPE_SWITCHED,
    FIRST_PENCIL_PROGRAM_CLEARED,
    FIRST_PENCIL_PROGRAM_SELECTED,
    FIRST_PENCIL_PROGRAMS_BACK,
    FIRST_PENCIL_PROGRAMS_FAILURE,
    FIRST_PENCIL_PROGRAMS_LOAD_STARTED,
    FIRST_PENCIL_PROGRAMS_SUCCESS,
    FIRST_PENCIL_SETUP_MODE_SELECTED,
    FIRST_PENCIL_TEMPLATE_SELECTED,
    FIRST_PENCIL_VEHICLE_SELECTED
} from '../actionTypes/firstPencilActionTypes';

// utils
import { isApplicableManualIncentive } from '../utils/firstPencilUtils';

const InitialState: FirstPencilState = { pendingCreate: false };

export type FirstPencilReducer = Reducer<FirstPencilState>;

const isCurrentLoad = (
    programs: FirstPencilProgramsState | undefined,
    loadId: string | undefined
): programs is FirstPencilProgramsState => programs !== undefined && programs.loadId === loadId;

const reduceProgramsSuccess = (programs: FirstPencilProgramsState, action: any): FirstPencilProgramsState => {
    const { offerType, requestedTier, primary, creditScoreOverride } = action.meta;
    const isNonPrimaryTier = requestedTier != null && primary !== true;
    const response = action.payload?.[offerType];
    const payments = (response?.payments ?? []) as IResponsePayment[];
    const tagged =
        requestedTier != null || creditScoreOverride != null
            ? payments.map((payment) => ({
                  ...payment,
                  ...(requestedTier != null && { requestedTier }),
                  ...(creditScoreOverride != null && { creditScoreOverride })
              }))
            : payments;
    return {
        ...programs,
        quotes: [...programs.quotes, ...tagged],
        incentives: isNonPrimaryTier ? programs.incentives : response?.incentives ?? programs.incentives,
        pendingRequests: Math.max(programs.pendingRequests - 1, 0)
    };
};

const hasProgramsPathSelection = (state: FirstPencilState): boolean =>
    [state, ...Object.values(state.stashedPrograms ?? {})].some(
        (tab) => Boolean(tab.selectedProgram) || (tab.manualIncentives ?? []).some(isApplicableManualIncentive)
    );

export const reducer = (state: FirstPencilState = InitialState, action: any): FirstPencilState => {
    switch (action.type) {
        case FIRST_PENCIL_INTERCEPT_OPENED: {
            // Reset, not spread — a previous deal's selection must not reopen pre-selected.
            return { ...InitialState, pendingCreate: true };
        }
        case FIRST_PENCIL_SETUP_MODE_SELECTED: {
            const { setupMode } = action.payload;
            if (setupMode === state.setupMode) {
                return state;
            }
            // Discard the other mode's selection so Create cannot apply an abandoned one.
            return {
                ...state,
                setupMode,
                selectedTemplateId: undefined,
                selectedTemplateName: undefined,
                selectedProgram: undefined,
                selectedIncentives: undefined,
                incentiveOverrides: undefined,
                manualIncentives: undefined,
                stashedPrograms: undefined,
                programsFilters: undefined,
                activeProgramsOfferType: undefined,
                applyError: undefined
            };
        }
        case FIRST_PENCIL_PROGRAMS_BACK: {
            if (state.setupMode !== 'programs' || hasProgramsPathSelection(state)) {
                return state;
            }
            return {
                ...state,
                setupMode: undefined,
                selectedProgram: undefined,
                selectedIncentives: undefined,
                incentiveOverrides: undefined,
                manualIncentives: undefined,
                stashedPrograms: undefined,
                programsFilters: undefined,
                activeProgramsOfferType: undefined
            };
        }
        case FIRST_PENCIL_TEMPLATE_SELECTED: {
            const { templateId, templateName } = action.payload;
            return { ...state, selectedTemplateId: templateId, selectedTemplateName: templateName, applyError: undefined };
        }
        case FIRST_PENCIL_VEHICLE_SELECTED: {
            const { vehicle } = action.payload;
            return {
                ...state,
                selectedVehicle: vehicle,
                selectedTemplateId: undefined,
                selectedTemplateName: undefined,
                selectedProgram: undefined,
                selectedIncentives: undefined,
                incentiveOverrides: undefined,
                manualIncentives: undefined,
                stashedPrograms: undefined,
                programsFilters: undefined,
                activeProgramsOfferType: undefined,
                applyError: undefined
            };
        }
        case FIRST_PENCIL_PROGRAM_SELECTED: {
            const { program, offerType } = action.payload;
            return {
                ...state,
                selectedProgram: { program, offerType },
                selectedIncentives: undefined,
                incentiveOverrides: undefined,
                applyError: undefined
            };
        }
        case FIRST_PENCIL_PROGRAM_CLEARED: {
            return {
                ...state,
                selectedProgram: undefined,
                selectedIncentives: undefined,
                incentiveOverrides: undefined,
                applyError: undefined
            };
        }
        case FIRST_PENCIL_OFFER_TYPE_SWITCHED: {
            const { offerType, tabLeft } = action.payload as {
                offerType: FirstPencilProgramsOfferType;
                tabLeft?: FirstPencilProgramsTabLeft;
            };
            const { selectedProgram, selectedIncentives, manualIncentives, incentiveOverrides } = state;
            const programsFilters = tabLeft
                ? { ...state.programsFilters, [tabLeft.offerType]: { filters: tabLeft.filters, dealTier: tabLeft.dealTier } }
                : state.programsFilters;
            const stashedPrograms = { ...state.stashedPrograms };
            const leftOfferType = (selectedProgram?.offerType ?? tabLeft?.offerType ?? state.activeProgramsOfferType) as
                | FirstPencilProgramsOfferType
                | undefined;
            if (leftOfferType && (selectedProgram || manualIncentives?.length)) {
                stashedPrograms[leftOfferType] = {
                    selectedProgram,
                    selectedIncentives,
                    manualIncentives,
                    incentiveOverrides
                };
            }
            const restored = stashedPrograms[offerType];
            delete stashedPrograms[offerType];
            return {
                ...state,
                selectedProgram: restored?.selectedProgram,
                selectedIncentives: restored?.selectedIncentives,
                manualIncentives: restored?.manualIncentives,
                incentiveOverrides: restored?.incentiveOverrides,
                stashedPrograms,
                programsFilters,
                activeProgramsOfferType: offerType,
                applyError: undefined
            };
        }
        case FIRST_PENCIL_MANUAL_INCENTIVE_ADDED: {
            return { ...state, manualIncentives: [...(state.manualIncentives ?? []), action.payload] };
        }
        case FIRST_PENCIL_MANUAL_INCENTIVE_UPDATED: {
            const { editId, changes } = action.payload;
            return {
                ...state,
                manualIncentives: (state.manualIncentives ?? []).map((incentive) =>
                    incentive.editId === editId ? { ...incentive, ...changes } : incentive
                )
            };
        }
        case FIRST_PENCIL_INCENTIVE_OVERRIDDEN: {
            const override = action.payload as FirstPencilIncentiveOverride;
            return {
                ...state,
                incentiveOverrides: { ...state.incentiveOverrides, [override.incentiveId]: override },
                applyError: undefined
            };
        }
        case FIRST_PENCIL_INCENTIVES_SELECTED: {
            return { ...state, selectedIncentives: action.payload, applyError: undefined };
        }
        case FIRST_PENCIL_PROGRAMS_LOAD_STARTED: {
            const { offerType, loadId, requestCount } = action.payload;
            return { ...state, programs: { offerType, loadId, quotes: [], pendingRequests: requestCount } };
        }
        case FIRST_PENCIL_PROGRAMS_SUCCESS: {
            if (!isCurrentLoad(state.programs, action.meta?.loadId)) {
                return state;
            }
            return { ...state, programs: reduceProgramsSuccess(state.programs, action) };
        }
        case FIRST_PENCIL_PROGRAMS_FAILURE: {
            if (!isCurrentLoad(state.programs, action.meta?.loadId)) {
                return state;
            }
            const { programs } = state;
            return {
                ...state,
                programs: { ...programs, error: action.payload ?? true, pendingRequests: Math.max(programs.pendingRequests - 1, 0) }
            };
        }
        case FIRST_PENCIL_CREATE_CONFIRMED: {
            // Stay open while the middleware applies the recorded selection; APPLY_SUCCEEDED closes it.
            return { ...state, applying: true, applyError: undefined };
        }
        case bootstrapActionTypes.BOOTSTRAP_FAILURE: {
            if (!state.applying) {
                return state;
            }
            return { ...state, applying: false, applyError: true };
        }
        case FIRST_PENCIL_APPLY_FAILED: {
            return { ...state, applying: false, applyError: true };
        }
        case FIRST_PENCIL_APPLY_SUCCEEDED:
        case FIRST_PENCIL_CANCELLED: {
            // Drop the selection — nothing should apply a cancelled path.
            return { ...InitialState };
        }
        default: {
            return state;
        }
    }
};
